import {
  useGetDefaultMapConfigQuery,
  useGetMapLayerConfigsQuery,
  useGetOrgsQuery,
  useHasValidLicenseQuery,
} from '@kinetic-ui/data-layer';
import {
  Dialog,
  LayerExtentUserPreferences,
  PREFERENCE_KEYS,
  sessionTenantManager,
  UserContext,
  useUserContext,
  useUserKineticPreference,
  useUserTenantPreference,
} from '@kinetic-ui/shared';
import { Button, Typography } from '@mui/material';
import React, { memo, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import LoadingScreen from '../loaders/LoadingScreen';
import Theme from '../Theme';
import { useInitializeTenant } from '../useInitializeTenant';
import { AppPreloadProps, SelectedOrgPreference } from './types';

const MINIMUM_LOAD_TIME_MS = 1250;

/**
 * Load tenant and user configurations that we want to wait for before mounting the app
 */
const AppPreload: React.FunctionComponent<AppPreloadProps> = ({ children }) => {
  const userContextValue = useUserContext();
  const [loadComplete, setLoadComplete] = useState(false);
  const startRef = useRef(new Date().getTime());
  const { t } = useTranslation('app');
  const auth = useAuth();
  const defaultOrgPreference = useUserKineticPreference<SelectedOrgPreference>({
    key: PREFERENCE_KEYS.DEFAULT_ORGANIZATION,
  });

  const orgsResult = useGetOrgsQuery();
  const currentOrganization = sessionTenantManager.getCurrentOrganization();

  // Load the default map layer config into cache.
  const { loading: licenseKeyLoading } = useHasValidLicenseQuery();
  const { loading: mapConfigLoading } = useGetDefaultMapConfigQuery({ skip: !currentOrganization });
  const { loading: mapLayerConfigLoading } = useGetMapLayerConfigsQuery({
    skip: !currentOrganization,
  });
  const { loading: layerExtendLoading } = useUserTenantPreference<LayerExtentUserPreferences>({
    key: PREFERENCE_KEYS.EXTENT_LAYERS,
  });

  useInitializeTenant({
    allOrganizations: orgsResult.data?.organizations,
    defaultOrgPreference,
  });

  const { loading: defaultLoading } = defaultOrgPreference;
  // Add any extra loading variables here to validate we are done loading.
  const loadingAny = !![
    mapConfigLoading,
    defaultLoading,
    orgsResult.loading,
    mapLayerConfigLoading,
    layerExtendLoading,
    licenseKeyLoading,
  ].filter(Boolean).length;

  useEffect(() => {
    let loadScreen: ReturnType<typeof setTimeout>;
    const now = new Date().getTime();

    if (userContextValue && !loadingAny) {
      loadScreen = setTimeout(
        () => setLoadComplete(true),
        Math.max(0, MINIMUM_LOAD_TIME_MS - (now - startRef.current))
      );
    }
    return () => {
      if (loadScreen) {
        clearTimeout(loadScreen);
      }
    };
  }, [defaultLoading, loadingAny, mapConfigLoading, orgsResult.loading, userContextValue]);

  const isUnauthorizedTenant =
    orgsResult.error?.graphQLErrors?.[0]?.extensions?.errorCode === 'userNotAssignedToTenant';

  const handleLogout = async () => {
    auth.signoutRedirect();
    await auth.removeUser();
    window.sessionStorage.clear();
    window.localStorage.clear();
    sessionTenantManager.clearCurrentOrganization();
  };

  return (
    <UserContext.Provider value={userContextValue}>
      <Suspense fallback={<LoadingScreen />}>
        {!loadComplete && <LoadingScreen />}
        {loadComplete && children}
      </Suspense>
      <Theme>
        <Dialog
          open={isUnauthorizedTenant}
          title={t('accessDenied')}
          subtitle=''
          buttons={
            <Button variant='contained' onClick={handleLogout}>
              {t('ok')}
            </Button>
          }
        >
          <Typography>{t('userNotAssignedToTenantMessage')}</Typography>
        </Dialog>
      </Theme>
    </UserContext.Provider>
  );
};

export default memo(AppPreload);
